import type { GetOneResult, RaRecord } from "ra-core";

import { getSupabaseClient } from "../providers/supabase/supabase";

/**
 * Access to the Bezirksversammlung data in the MCP (public.member_roles.bv):
 * null = none, "admin" = every Drucksache, list = Gremien name substrings.
 */
export type BvAccess = "admin" | string[] | null;

export type BvMode = "none" | "admin" | "list";

export type BvFormFields = {
  bv_mode?: BvMode;
  /** One Gremium substring per line, only used when bv_mode is "list". */
  bv_list?: string;
};

export const bvToForm = (bv: BvAccess | undefined): Required<BvFormFields> => {
  if (bv === "admin") return { bv_mode: "admin", bv_list: "" };
  if (Array.isArray(bv) && bv.length) {
    return { bv_mode: "list", bv_list: bv.join("\n") };
  }
  return { bv_mode: "none", bv_list: "" };
};

/** undefined leaves the stored value alone (form without the BV fields). */
export const formToBv = (data: BvFormFields): BvAccess | undefined => {
  if (!data.bv_mode) return undefined;
  if (data.bv_mode === "none") return null;
  if (data.bv_mode === "admin") return "admin";
  const list = [
    ...new Set(
      (data.bv_list ?? "")
        .split("\n")
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  ];
  return list.length ? list : null;
};

/**
 * Adds the BV fields to a sales record. RLS lets administrators read every
 * member_roles row and everybody else only their own.
 */
export const withBvFields = async <T extends RaRecord & { user_id?: string }>(
  result: GetOneResult<T>,
): Promise<GetOneResult<T & BvFormFields>> => {
  const userId = result.data.user_id;
  if (!userId) return result;
  const { data } = await getSupabaseClient()
    .from("member_roles")
    .select("bv")
    .eq("user_id", userId)
    .maybeSingle();
  return { ...result, data: { ...result.data, ...bvToForm(data?.bv) } };
};
